import { BaseApi } from './base';
import type { ContributionInfo, Purchase, ShopItem } from './types';

export interface CreateShopItemPayload {
  title: string;
  description?: string;
  price: number;
  icon?: string;
  purchaseType?: 'standard' | 'collectable';
}

export interface UpdateShopItemPayload {
  title?: string;
  description?: string;
  price?: number;
  icon?: string;
  purchaseType?: 'standard' | 'collectable';
}

export interface ContributeResponse {
  contribution: {
    _id: string;
    shopItem: string;
    contributor: string;
    amount: number;
    createdAt: string;
  };
  totalCollected: number;
  remaining: number;
  isFullyFunded: boolean;
}

export class ShopApi extends BaseApi {
  public async purchaseItem(itemId: string, quantity: number): Promise<void> {
    await this._request<void>('/shop/request', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        itemId,
        quantity,
      }),
    });
  }

  public async createItem(payload: CreateShopItemPayload): Promise<ShopItem> {
    return await this._request<ShopItem>('/shop/item', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async getItemById(itemId: string): Promise<ShopItem> {
    return await this._request<ShopItem>(`/shop/item/${itemId}`, {
      method: 'GET',
    });
  }

  public async updateItem(
    itemId: string,
    payload: UpdateShopItemPayload,
  ): Promise<ShopItem> {
    return await this._request<ShopItem>(`/shop/item/${itemId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async deleteItem(itemId: string): Promise<void> {
    await this._request<void>(`/shop/item/${itemId}`, {
      method: 'DELETE',
    });
  }

  public async getItems(): Promise<ShopItem[]> {
    return await this._request<ShopItem[]>('/shop/items', {
      method: 'GET',
    });
  }

  public async getAllPurchases(): Promise<Purchase[]> {
    return await this._request<Purchase[]>('/shop/purchases', {
      method: 'GET',
    });
  }

  public async approvePurchase(purchaseId: string): Promise<Purchase> {
    return await this._request<Purchase>(
      `/shop/purchases/${purchaseId}/approve`,
      {
        method: 'POST',
      },
    );
  }

  public async deletePurchase(purchaseId: string): Promise<void> {
    await this._request<void>(`/shop/purchases/${purchaseId}`, {
      method: 'DELETE',
    });
  }

  public async contribute(
    itemId: string,
    amount: number,
  ): Promise<ContributeResponse> {
    return await this._request<ContributeResponse>('/shop/contribute', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ itemId, amount }),
    });
  }

  public async getContributions(itemId: string): Promise<ContributionInfo> {
    return await this._request<ContributionInfo>(
      `/shop/item/${itemId}/contributions`,
      {
        method: 'GET',
      },
    );
  }

  public async reorderItems(
    items: Array<{ id: string; position: number }>,
  ): Promise<void> {
    await this._request<void>('/shop/items/reorder', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
  }
}
